// @flow
import React, {useRef} from 'react';
import PropTypes from 'prop-types';
import {IconButton, makeStyles, Typography} from '@material-ui/core';

import addImage from '../../../../../assets/img/legal/add-image.svg';
import {colors} from '../../../../../assets/styles/colors';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {CancelOutlined} from '@material-ui/icons';

type Props = {
  idx: number,
  expand?: boolean,
  disabled?: boolean,
  images: string[],
  handleRemoveImage: (idx: number) => void,
  handleChangeImage: (
    e: SyntheticInputEvent<HTMLInputElement>,
    idx: number
  ) => Promise<any>,
  placeholder: string,
  maxHeight?: number,
};

const useStyles = props =>
  makeStyles(theme => ({
    imageBoxDashed: {
      height: props && props.expand ? props.maxHeight : '100%',
      maxHeight: '100%',
      width: '100%',
      border: `2px dashed ${colors.grey500}`,
      backgroundColor: '#FFFFFF',
      boxSizing: 'border-box',
      borderRadius: '12px',
      padding: theme.spacing(3, 2),
      textAlign: 'center',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      color: colors.purple3,
      '&:focus-within': {
        border: `2px dashed ${theme.palette.primary.main}`,
      },
    },
    defaultCursor: {
      cursor: 'unset',
    },
    imageBox: {
      height: props && props.expand ? props.maxHeight : '100%',
      // height: '100%',
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid #E8E8E9',
      boxSizing: 'border-box',
      borderRadius: theme.borderRadius,
      padding: theme.spacing(2),
      textAlign: 'center',
      position: 'relative',
      eight: props && props.maxHeight ? props.maxHeight : '100%',
    },
    removeImageIcon: {
      position: 'absolute',
      top: 4,
      right: 4,
    },
    image: {
      display: 'block',
      margin: 'auto',
      height: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
      aspectRatio: 'cover',
    },
    imageUploadTip: {
      marginTop: theme.spacing(1),
      color: '#9595AB',
      fontSize: '14px',
    },
    button: {
      height: '100%',
      width: '100%',
      padding: 0,
      '& span': {
        height: '100%',
      },
    },
  }));

export function ImageButton(props: Props) {
  const {
    disabled = false,
    idx,
    expand = false,
    images,
    handleChangeImage,
    handleRemoveImage,
    placeholder,
    maxHeight,
  } = props;
  const classes = useStyles({maxHeight, expand})();

  const inputFileRef = useRef();

  const handleAddImageClick = () => {
    if (!disabled && inputFileRef.current) {
      inputFileRef.current.value = '';
      inputFileRef.current.click();
    }
  };

  const handleRemoveFileImage = () => {
    if (!disabled) handleRemoveImage(idx);
  };

  const onImageChange = (e: SyntheticInputEvent<HTMLInputElement>) => {
    handleChangeImage(e, idx);
  };

  if (images && images[idx]) {
    return (
      <div className={classes.imageBox}>
        <img className={classes.image} src={images[idx]} alt="compressed" />
        <IconButton
          aria-label={'cancel'}
          className={classes.removeImageIcon}
          onClick={handleRemoveFileImage}
        >
          <CancelOutlined />
        </IconButton>
      </div>
    );
  }
  return (
    <>
      <CustomButton
        disabled={disabled}
        onClick={handleAddImageClick}
        className={classes.button}
      >
        <div className={classes.imageBoxDashed}>
          <img src={addImage} alt="add btn" />
          <Typography className={classes.imageUploadTip}>
            {placeholder}
          </Typography>
        </div>
      </CustomButton>
      <input
        ref={inputFileRef}
        hidden
        accept="image/png, image/jpeg"
        type="file"
        onChange={onImageChange}
      />
    </>
  );
}

ImageButton.propTypes = {
  idx: PropTypes.number,
  expand: PropTypes.bool,
  disabled: PropTypes.bool,
  images: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
  maxHeight: PropTypes.number,
  handleChangeImage: PropTypes.func.isRequired,
  handleRemoveImage: PropTypes.func.isRequired,
};
